import { Link } from "react-router-dom";
import { Mail, Phone, MessageCircle } from "lucide-react";
import { Logo } from "./Header";
import { EMAIL, PHONE, PHONE_TEL, waLink } from "@/data/site";
import { MAIN_SERVICES } from "@/data/services";
import { SECTORS } from "@/data/sectors";
import { LOCATIONS } from "@/data/locations";
import { usePublicSettings } from "@/lib/publicSettings";

const Col = ({ title, children }) => (
  <>
    <details className="group border-b border-slate-200 lg:hidden">
      <summary className="flex cursor-pointer items-center justify-between py-4 text-sm font-semibold text-slate-900">{title}<span className="text-blue-600 transition-transform group-open:rotate-45">+</span></summary>
      <ul className="space-y-2.5 pb-5 text-sm">{children}</ul>
    </details>
    <div className="hidden lg:block">
      <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">{title}</p>
      <ul className="space-y-2.5 text-sm">{children}</ul>
    </div>
  </>
);
const L = ({ to, children }) => <li><Link to={to} className="text-slate-700 transition-colors hover:text-blue-700">{children}</Link></li>;

function LegalLine() {
  const { company } = usePublicSettings();
  const parts = [
    `Servizi forniti da ${company.legal_name || "Nicole Srl"}`,
    company.legal_address && `Sede legale: ${company.legal_address}`,
    company.vat && `P.IVA ${company.vat}`,
    company.tax_code && `C.F. ${company.tax_code}`,
    company.rea && `REA ${company.rea}`,
    company.share_capital && `Capitale sociale ${company.share_capital}`,
    company.pec && `PEC ${company.pec}`,
    company.other,
  ].filter(Boolean);
  return <p data-testid="footer-legal-info" className="text-xs leading-relaxed text-slate-600">STUDIO WEB NS è un marchio commerciale. {parts.join(" · ")}.</p>;
}

export default function Footer() {
  const { sedi } = usePublicSettings();
  return (
    <footer data-testid="site-footer" className="bg-p-footer border-t border-blue-100 pb-24 pt-14 text-slate-700 lg:pb-10 lg:pt-20">
      <div className="wrap">
        <div className="grid gap-0 lg:grid-cols-[1.4fr_1fr_1fr_1fr] lg:gap-12">
          <div className="mb-6 lg:mb-0">
            <Logo light={false} size="lg" />
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-slate-600">Siti web professionali, cataloghi digitali, CRM e gestione social per attività e professionisti in tutta Italia.</p>
            <div className="mt-6 space-y-2.5 text-sm">
              <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-blue-700"><Mail className="h-4 w-4 text-blue-600" />{EMAIL}</a>
              <a href={`tel:${PHONE_TEL}`} data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-blue-700"><Phone className="h-4 w-4 text-blue-600" />{PHONE}</a>
              <a href={waLink("preventivo")} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2.5 hover:text-blue-700"><MessageCircle className="h-4 w-4 text-blue-600" />WhatsApp</a>
            </div>
          </div>
          <Col title="Servizi">
            {MAIN_SERVICES.map((s) => <L key={s.slug} to={`/servizi/${s.slug}`}>{s.name}</L>)}
          </Col>
          <Col title="Soluzioni">
            {SECTORS.map((s) => <L key={s.slug} to={`/soluzioni/${s.slug}`}>{s.name}</L>)}
            <L to="/servizi/siti-web-piccole-imprese">Piccole imprese</L>
            <L to="/servizi/siti-web-negozi">Negozi</L>
          </Col>
          <Col title="Agenzia">
            <L to="/prezzi">Prezzi</L>
            <L to="/chi-siamo">Chi siamo</L>
            <L to="/guide">Guide</L>
            <L to="/consulenza-online">Consulenza online gratuita</L>
            <L to="/contatti">Contatti</L>
            {LOCATIONS.map((l) => <L key={l.slug} to={`/${l.slug}`}>{l.h1}</L>)}
          </Col>
        </div>

        <div className="mt-8 grid grid-cols-3 gap-4 border-t border-slate-200 pt-8 lg:mt-16 lg:pt-10">
          {sedi.map((s) => (
            <div key={s.city} data-testid={`footer-sede-${s.city.toLowerCase()}`}>
              <p className="font-display text-sm font-semibold text-slate-900">{s.city}</p>
              <p className="mt-1 text-xs text-slate-700 sm:text-sm">{s.address}</p>
              {s.by_appointment && <p className="mt-1 text-xs text-slate-600">Su appuntamento</p>}
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-col gap-6 border-t border-slate-200 pt-6 lg:mt-12 lg:pt-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl space-y-2">
            <p className="text-xs text-slate-600">© {new Date().getFullYear()} STUDIO WEB NS. Prezzi IVA esclusa.</p>
            <LegalLine />
          </div>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
            <li><Link to="/privacy-policy" className="hover:text-blue-700">Privacy</Link></li>
            <li><Link to="/cookie-policy" className="hover:text-blue-700">Cookie</Link></li>
            <li><button type="button" data-testid="footer-cookie-prefs" onClick={() => window.dispatchEvent(new Event("open-cookie-prefs"))} className="hover:text-blue-700">Preferenze cookie</button></li>
            <li><Link to="/condizioni-generali" className="hover:text-blue-700">Condizioni</Link></li>
            <li><Link to="/proprieta-e-disdetta" className="hover:text-blue-700">Proprietà e disdetta</Link></li>
            <li><Link to="/richieste-dati-personali" className="hover:text-blue-700">Richieste dati</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
